<template>
  <div class="god_page_container">
    <div class="page-title-row">
      <h2>Gestión de Usuarios</h2>
      <div class="flex gap-sm">
        <button @click="exportUsersXLS()" class="secondary_button">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Exportar a Excel
        </button>
        <button @click="addUser()" class="main_button-green">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Añadir usuario
        </button>
      </div>
    </div>

    <div id="selector_container">
      <input
        type="text"
        class="input_200"
        v-model="user_email"
        placeholder="Email del usuario"
        @keyup.enter="getUsers()"
      />

      <input
        type="text"
        class="input_200"
        v-model="user_name"
        placeholder="Nombre del usuario"
        @keyup.enter="getUsers()"
      />

      <button @click="getUsers()" class="main_button" style="min-width: 140px;">
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        Buscar
      </button>
    </div>

    <div class="table-container">
      <table>
        <thead>
          <tr>
              <th class="small">ID</th>
              <th class="big">Email</th>
              <th class="big">Nombre</th>
              <th class="small">Tipo / Estado</th>
              <th class="small">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="(item, index) in users"
            :key="item.ID"
            :class="index % 2 === 0 ? 'bg-gray-100' : 'bg-white'"
          >
            <td><strong>#{{ item.ID }}</strong></td>
            <td style="text-align: left;">{{ item.user_email }}</td>
            <td style="text-align: left; font-weight: 500;">{{ item.name }}</td>
            <td>
              <span class="badge" :class="item.activo ? 'badge-success' : 'badge-danger'">
                {{ traducirTipo(item.user_type, item.activo) }}
              </span>
            </td>
            <td>
              <div class="actions-cell">
                <img class="action_icon" title="Editar" src="@/assets/images/ic_reservation_edit.png" @click="onEditClicked(item)"/>
                <img class="action_icon" title="Reservas" src="@/assets/images/ic_action_reservations.png" @click="onReservationsClicked(item)"/>
                <img class="action_icon" title="Monedero" src="@/assets/images/ic_action_wallet.png" @click="onWalletClicked(item)"/>
                <img class="action_icon" title="Eliminar" src="@/assets/images/ic_action_delete.png" @click="onHideClicked(item)"/>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="users.length === 0" class="empty-state">
      <p>No se han encontrado usuarios para esta búsqueda</p>
    </div>

    <ErrorPopup :errorMessage="error" />
    <TwoButtonPopup
      :title="twoButtonTitle"
      :message="twoButtonMessage"
      :acceptButton="twoButtonAcceptButton"
      @acceptCallback="() => twoButtonConfirmed()"
      :cancelButton="twoButtonCancelButton"
      @cancelCallback="() => twoButtonCancelled()"
      />

  </div>

</template>

<script setup>

  import { ref, nextTick, watch } from 'vue'
  import { useLoadingStore } from "@/stores/loadingStore";
  import { useRouter, useRoute } from 'vue-router';
  import { refreshToken } from "@/services/authService";
  import ErrorPopup from "@/components/common/ErrorPopup.vue";
  import TwoButtonPopup from "@/components/common/TwoButtonPopup.vue";
  import * as XLSX from "xlsx";
  import { saveAs } from "file-saver";

  const error = ref(null);
  const users = ref([]);
  const loadingStore = useLoadingStore();
  const router = useRouter();
  const route = useRoute();
  const user_email = ref(route.query.user_email || '');
  const user_name = ref(route.query.user_name || '');

  watch([user_email, user_name], () => {
    router.replace({
      query: {
        ...route.query,
        user_email: user_email.value,
        user_name: user_name.value
      }
    });
  });

  const twoButtonTitle = ref("");
  const twoButtonMessage = ref("");
  const twoButtonAcceptButton = ref("");
  const twoButtonCancelButton = ref("");
  let twoButtonConfirmed = null;
  let twoButtonCancelled = null;

  const getUsers = async () => {
    if (user_email.value === '' && user_name.value === '') {
      showError("Por favor, indica un email o un nombre para buscar.");
      return;
    }
    loadingStore.setLoading(true);
    error.value = null;

    try {
      let response = await getUsersInAPI();

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await getUsersInAPI();
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        showError("Se ha producido un error buscando usuarios.");
        return;
      }

      const data = await response.json();

      if (data.errors) {
        error.value = data.message;
        users.value = [];
      } else {
        error.value = null;
        users.value = data.users;
      }
    } catch (exception) {
      error.value = exception.message;
    } finally {
      loadingStore.setLoading(false);
    }
  };

  async function getUsersInAPI() {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/users/get?user_id="+localStorage.getItem('user_id')+"&user_email="+user_email.value+"&user_name="+user_name.value, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
      });
  }

  const showError = async (message) => {
    error.value = null;
    await nextTick();
    error.value = message;
  };

  const onEditClicked = (item) => {
    router.push({ name: 'UserDetails', params: { userid: item.ID } });
  };

  const onReservationsClicked = (item) => {
    router.push({ name: 'Reservations', query: { user_email: item.user_email } });
  };

  const onWalletClicked = (item) => {
    router.push({ name: 'Wallet', query: { user_email: item.user_email } });
  };

  const onHideClicked = async (item) => {
    twoButtonTitle.value = item.activo ? "Desactivar usuario" : "Activar usuario";
    twoButtonMessage.value = item.activo ? "¿Estás seguro de que deseas desactivar este usuario?" : "¿Estás seguro de que deseas activar de nuevo al usuario?";
    twoButtonAcceptButton.value = "Aceptar";
    twoButtonCancelButton.value = "Cancelar";
    twoButtonConfirmed = () => {
      twoButtonTitle.value = "";
      twoButtonMessage.value = "";
      twoButtonAcceptButton.value = "";
      twoButtonCancelButton.value = "";
      changeUserVisibilityConfirmed(item);
    };
    twoButtonCancelled = () => {
      twoButtonTitle.value = "";
      twoButtonMessage.value = "";
      twoButtonAcceptButton.value = "";
      twoButtonCancelButton.value = "";
    }
  }

  const changeUserVisibilityConfirmed = async (item) => {
    loadingStore.setLoading(true);
    error.value = null;

    try {
      let response = await changeUserVisibilityInAPI(item);

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await changeUserVisibilityInAPI(item);
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        loadingStore.setLoading(false);
        showError("Se ha producido un error cambiando el estado del usuario.");
        return;
      }

      const data = await response.json();

      if (data.errors) {
        error.value = data.message;
      } else {
        error.value = null;
      }
      getUsers();
    } catch (exception) {
      error.value = exception.message;
    }
  };

  const changeUserVisibilityInAPI = async (item) => {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/users/edit_visibility", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
        body: JSON.stringify({
          "user_id": localStorage.getItem('user_id'),
          "user_to_edit": item.ID,
          "visibility": item.activo ? false : true
        }),
      });
  };

  function traducirTipo(userType, active) {
    if (userType === 'empresa') {
      if (active) {
        return 'Empresa';
      } else {
        return '🚫 Empresa 🚫';
      }
    } else {
      if (active) {
        return 'Usuario';
      } else {
        return '🚫 Usuario 🚫';
      }
    }
  }

  async function addUser() {
    router.push({ name: 'NewUser' });
  }

  const exportUsersXLS = async () => {
    loadingStore.setLoading(true);
    try {
      let response = await getAllUsersInAPI();

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await getAllUsersInAPI();
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        showError("Ha ocurrido un error generando el Excel con los usuarios.");
        return;
      }

      const data = await response.json();

      if (data.errors) {
        showError("Ha ocurrido un error generando el Excel con los usuarios.");
      } else {
        exportXLS(data.users);
      }
    } catch (exception) {
        showError("Ha ocurrido un error generando el Excel con los usuarios.");
    } finally {
      loadingStore.setLoading(false);
    }
  }

  const exportXLS = (users) => {
    const flatUsersData = [];
    const flatCompaniesData = [];

    users.filter(user => user.user_type === 'usuario').forEach(user => {
      let entry = {
        "Email": user.user_email,
        "Nombre": user.name,
        "Activo": user.activo ? "SI" : "NO",
        "Ciudad": user.city
      };

      flatUsersData.push(entry);
    });

    users.filter(user => user.user_type === 'empresa').forEach(user => {
      let entry = {
        "Email": user.user_email,
        "Nombre": user.name,
        "Activo": user.activo ? "SI" : "NO",
        "Nombre facturacion": user.invoice_name,
        "Dirección facturacion": user.invoice_address,
        "Ciudad facturacion": user.invoice_city,
        "CIF": user.invoice_cif,
      };

      flatCompaniesData.push(entry);
    });

      // Crear la hoja de Excel
    const worksheetUsers = XLSX.utils.json_to_sheet(flatUsersData);
    const worksheetCompanies = XLSX.utils.json_to_sheet(flatCompaniesData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheetUsers, "Usuarios");
    XLSX.utils.book_append_sheet(workbook, worksheetCompanies, "Empresas");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(data, "users_god.xlsx");
  }

  async function getAllUsersInAPI() {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/users/get_to_export?user_id="+localStorage.getItem('user_id'), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
      });
  }

</script>

<style scoped>
.actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.big {
  width: 28%;
}

.small {
  width: 12%;
}

.empty-state {
  background: #FFFFFF;
  border: 1px dashed var(--border-color);
  border-radius: var(--radius-lg);
  padding: 2.5rem;
  text-align: center;
  color: var(--text-color-light);
  margin-top: 1.5rem;
}

.empty-state p {
  margin-bottom: 0;
  font-weight: 500;
}

@media (max-width: 768px) {
  #selector_container > * {
    flex: none !important;
    width: 100%;
  }

  .actions-cell {
    flex-wrap: wrap;
    gap: 6px;
  }
}
</style>
